import { Fragment, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Stack, Typography } from '@mui/material'
import _ from 'lodash'
import {
  ConfirmableButton,
  DeleteButton,
  Picto,
  PictoGroup,
  RenderCount,
  Section,
  Subtitle,
  Table,
  Title,
} from '~/components'
import { useCanEdit } from '~/hooks'
import { useRouteContext } from '~/routes'
import { bshSlice } from '~/store'
import CategoryDetailsContext from './CategoryDetailsContext'
import { MoveButton, NewGroupOrProperty, PropertiesList } from './components'
import GroupContext from './GroupContext'
import { useEditableGroupList } from './hooks'

const CategoryDetails = () => {
  const dispatch = useDispatch()
  const canEdit = useCanEdit()
  const { t } = useTranslation()
  const { subCategory, product } = useRouteContext(CategoryDetails.name)

  const [selectedProductId, setSelectedProductId] = useState()

  const products = useSelector((state) =>
    bshSlice.products(state, subCategory?.products)
  )
  const isResettingProperties = useSelector((state) =>
    bshSlice.isPendingRequest(state, ['resetProperties'])
  )
  const {
    activeGroupId,
    groups,
    other,
    saveProperty,
    setActiveGroupId,
    onAttachProperty,
    onDetachProperty,
    onGroupMove,
    onGroupDrop,
    onPropertyMove,
    onPropertyDrop,
    onPropertyToggleSkip,
  } = useEditableGroupList({
    selectedProductId,
  })

  const title = useMemo(() => {
    if (product) {
      return (
        <Title
          actions={
            canEdit ? (
              <ConfirmableButton
                dialog={{
                  actions: { confirm: t('ui.rebuild') },
                  title: t('specifications.confirm.title'),
                  body: t('specifications.confirm.body'),
                }}
                label={t('specifications.button.label')}
                loading={!!isResettingProperties}
                size="small"
                title={t('specifications.button.title')}
                onConfirmed={() => {
                  dispatch(bshSlice.resetProperties({ data: product }))
                }}
              />
            ) : undefined
          }
        >
          {product.name}
        </Title>
      )
    } else {
      return <Title>{subCategory.name}</Title>
    }
  }, [dispatch, isResettingProperties, product, subCategory.name, t, canEdit])

  const productsList = useMemo(() => {
    if (product) return
    return (
      <Section
        sectionId={`${CategoryDetails.name}-products`}
        title={`${t('category-details.products.title')}:`}
      >
        <RenderCount />
        <Stack spacing={2}>
          <Table
            tableId="category-details"
            headers={[
              { content: t('product.id') },
              { content: t('product.name') },
            ]}
            hover
            index
            rows={products.map((product) => {
              const cells = [
                { content: product.code },
                {
                  content: (
                    <div className="relative">
                      {product.name}
                      <PictoGroup
                        className="absolute right-0 top-1/2 -translate-y-1/2"
                        text={product.status}
                      />
                    </div>
                  ),
                },
              ]
              if (canEdit) {
                cells.push(
                  {
                    content: (
                      <MoveButton product={product} title={t('product.move')} />
                    ),
                    narrow: true,
                    sx: { padding: 0 },
                  },
                  {
                    content: (
                      <DeleteButton
                        dialog={{
                          title: t('product.delete.confirm.title'),
                          body: t('product.delete.confirm.body'),
                        }}
                        title={t('product.delete.cta')}
                        onConfirmed={() => {
                          dispatch(bshSlice.deleteItem({ data: product }))
                        }}
                      />
                    ),
                    narrow: true,
                    sx: { padding: 0 },
                  }
                )
              }
              return { cells }
            })}
            selectable={{ multiselect: false }}
            onSelect={(selected) => {
              setSelectedProductId(
                selected !== undefined
                  ? subCategory.products[selected]
                  : undefined
              )
            }}
          />
          <Typography variant="body2">
            <span
              dangerouslySetInnerHTML={{
                __html: t('category-details.products.info', {
                  joinArrays: '<br />',
                }),
              }}
            />
            {_.toArray('MPO').map((letter, i) => (
              <Fragment key={i}>
                <br />
                <span>
                  <Picto letter={letter} />{' '}
                  {t(`category-details.products.picto.${letter.toLowerCase()}`)}
                </span>
              </Fragment>
            ))}
          </Typography>
        </Stack>
      </Section>
    )
  }, [dispatch, product, products, subCategory.products, t, canEdit])

  const propertiesList = useMemo(() => {
    if (!groups) return
    return (
      <Section
        sectionId={`${CategoryDetails.name}-properties`}
        title={product ? null : `${t('category-details.properties.title')}:`}
      >
        <RenderCount />
        <Stack spacing={2}>
          <CategoryDetailsContext.Provider
            value={{
              activeGroupId,
              saveProperty,
              onAttachProperty,
              onDetachProperty,
              onGroupMove,
              onGroupDrop,
              onPropertyMove,
              onPropertyDrop,
              onPropertyToggleSkip,
            }}
          >
            <Stack spacing={1}>
              <Subtitle variant="h3">
                {t('category-details.properties.used')}
                {product && (
                  <>
                    {' '}
                    <Typography
                      variant="small"
                      sx={{
                        color: (theme) => theme.palette.text.secondary,
                        fontWeight: 400,
                      }}
                    >
                      {t('category-details.properties.used-with-product', {
                        code: product.code,
                      })}
                    </Typography>
                  </>
                )}
              </Subtitle>
              {groups?.map((group, index) => (
                <GroupContext.Provider
                  key={group.id}
                  value={{
                    group,
                    onGroupActive: (id) => {
                      setActiveGroupId(id ?? group.id)
                    },
                  }}
                >
                  <PropertiesList
                    groupId={`${CategoryDetails.name}-${group.name}`}
                    index={index}
                    properties={group.properties}
                  />
                </GroupContext.Provider>
              ))}
              {groups?.length === 0 && canEdit && <NewGroupOrProperty />}
              <Typography variant="body2">
                {t('category-details.properties.info', { joinArrays: '\n' })
                  .split('\n')
                  .map((line, index) => {
                    const pattern = /picto=([A-Z])/
                    const match = pattern.exec(line)
                    let picto
                    if (match) {
                      line = line.replace(pattern, '')
                      picto = <Picto letter={match[1]} />
                    }
                    return (
                      <Fragment key={index}>
                        {index > 0 && <br />}
                        <span>
                          {line}
                          {picto}
                        </span>
                      </Fragment>
                    )
                  })}
                {_.toArray('MZ').map((letter, i) => (
                  <Fragment key={i}>
                    <br />
                    <span>
                      <Picto letter={letter} />{' '}
                      {t(
                        `category-details.properties.picto.${letter.toLowerCase()}`
                      )}
                    </span>
                  </Fragment>
                ))}
              </Typography>
            </Stack>
            {(groups?.length > 0 || other?.length > 0) && (
              <Stack spacing={1}>
                <Subtitle variant="h3">
                  {t('category-details.properties.other')}
                </Subtitle>
                <PropertiesList index={groups.length} properties={other} />
              </Stack>
            )}
          </CategoryDetailsContext.Provider>
        </Stack>
      </Section>
    )
  }, [
    groups,
    product,
    t,
    activeGroupId,
    saveProperty,
    onAttachProperty,
    onDetachProperty,
    onGroupMove,
    onGroupDrop,
    onPropertyMove,
    onPropertyDrop,
    onPropertyToggleSkip,
    other,
    setActiveGroupId,
    canEdit,
  ])

  return (
    <>
      {product && <Helmet title={product.code} />}
      <RenderCount />
      {title}
      <Stack spacing={2}>
        {productsList}
        {propertiesList}
      </Stack>
    </>
  )
}

export default CategoryDetails
